import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Send, Mail, User, Euro, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImpayesSection({ buildingId, token }) {
  const [impayes, setImpayes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCopro, setSelectedCopro] = useState(null);
  const [sendingRelance, setSendingRelance] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [selectedNiveau, setSelectedNiveau] = useState('niveau_1');

  const fetchImpayes = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comptabilite/impayes?building_id=${buildingId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setImpayes(data);
      }
    } catch (error) {
      console.error('Error fetching impayes:', error);
    } finally {
      setLoading(false);
    }
  }, [buildingId, token]);

  useEffect(() => {
    fetchImpayes();
  }, [fetchImpayes]);

  const handleSendRelance = async (copro, niveau = 'niveau_1') => {
    setSendingRelance(copro.coproprietaire_id);
    
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/relances`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          building_id: buildingId,
          coproprietaire_id: copro.coproprietaire_id,
          coproprietaire_nom: copro.coproprietaire_nom,
          coproprietaire_email: copro.email,
          montant_du: copro.solde_du,
          niveau: niveau,
          envoyer: true
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.email_envoye) {
          toast.success(`Relance envoyée à ${copro.coproprietaire_nom}`);
        } else {
          toast.warning(`Relance créée mais email non envoyé`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingRelance(null);
    }
  };

  const handleSendBulkRelances = async () => {
    setSendingBulk(true);
    
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/relances/masse`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          building_id: buildingId,
          niveau: selectedNiveau
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des relances');
    } finally {
      setSendingBulk(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const niveauLabels = {
    'niveau_1': { label: 'Rappel amiable', color: 'text-orange-600 bg-orange-100' },
    'niveau_2': { label: 'Relance ferme', color: 'text-red-500 bg-red-100' },
    'niveau_3': { label: 'Mise en demeure', color: 'text-red-700 bg-red-200' }
  };

  const totalImpayes = impayes.reduce((sum, i) => sum + (i.solde_du || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Impayés & Relances</h2>
          <p className="text-sm text-gray-500">
            {impayes.length} coproprietaire(s) avec solde débiteur • Total: {formatMoney(totalImpayes)}
          </p>
        </div>
        {impayes.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={selectedNiveau}
              onChange={(e) => setSelectedNiveau(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="niveau-relance-select"
            >
              <option value="niveau_1">Rappel amiable</option>
              <option value="niveau_2">Relance ferme</option>
              <option value="niveau_3">Mise en demeure</option>
            </select>
            <button
              onClick={handleSendBulkRelances}
              disabled={sendingBulk}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
              data-testid="send-bulk-relances-btn"
            >
              {sendingBulk ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
              Relancer tous ({impayes.length})
            </button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-red-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total impayés</p>
              <p className="text-2xl font-bold text-red-600">{formatMoney(totalImpayes)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-orange-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <User className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Copropriétaires concernés</p>
              <p className="text-2xl font-bold text-orange-600">{impayes.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-yellow-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Montant moyen</p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatMoney(impayes.length > 0 ? totalImpayes / impayes.length : 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {impayes.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Euro className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun impayé ! 🎉</h3>
            <p className="text-gray-500">Tous les coproprietaires sont à jour de leurs paiements.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Copropriétaire</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Appelé</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Payé</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Solde dû</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {impayes.map(copro => (
                <tr key={copro.coproprietaire_id} className="hover:bg-red-50/50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{copro.coproprietaire_nom}</p>
                      <p className="text-sm text-gray-500">{copro.email || 'Email non renseigné'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">{formatMoney(copro.montant_appele)}</td>
                  <td className="px-4 py-3 text-right text-green-600">{formatMoney(copro.montant_paye)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-semibold text-red-600">{formatMoney(copro.solde_du)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleSendRelance(copro, 'niveau_1')}
                        disabled={!copro.email || sendingRelance === copro.coproprietaire_id}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={copro.email ? 'Envoyer une relance' : 'Email non renseigné'}
                      >
                        {sendingRelance === copro.coproprietaire_id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <Mail className="h-4 w-4" />
                            Relancer
                          </>
                        )}
                      </button>
                      <select
                        onChange={(e) => handleSendRelance(copro, e.target.value)}
                        className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                        disabled={!copro.email}
                        defaultValue=""
                      >
                        <option value="" disabled>Niveau</option>
                        <option value="niveau_1">Rappel amiable</option>
                        <option value="niveau_2">Mise en demeure</option>
                        <option value="niveau_3">Dernier avis</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-red-50 font-semibold">
              <tr>
                <td className="px-4 py-3">TOTAL</td>
                <td className="px-4 py-3 text-right">{formatMoney(impayes.reduce((s, i) => s + i.montant_appele, 0))}</td>
                <td className="px-4 py-3 text-right text-green-600">{formatMoney(impayes.reduce((s, i) => s + i.montant_paye, 0))}</td>
                <td className="px-4 py-3 text-right text-red-600">{formatMoney(totalImpayes)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Tips */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <h4 className="font-medium text-blue-900 mb-2">💡 Conseils de recouvrement</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Niveau 1</strong> : Rappel amiable - à envoyer dès le dépassement de l'échéance</li>
          <li>• <strong>Niveau 2</strong> : Mise en demeure - 15 jours après le premier rappel</li>
          <li>• <strong>Niveau 3</strong> : Dernier avis avant contentieux - 30 jours après la mise en demeure</li>
          <li>• Au-delà, consultez un huissier ou un avocat pour le recouvrement judiciaire</li>
        </ul>
      </div>
    </div>
  );
}
